import React, { useState } from 'react';

import { useLisaIngestorStatus } from '../../api/lisa';
import type { IngestorRun } from '../../interfaces/lisa';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync } from '@fortawesome/free-solid-svg-icons';
import { formatUptime, formatDate } from './utils';

const INTERVAL_OPTIONS = [5, 10, 30, 60];

type Tab = 'recent' | 'skipped' | 'errors';

function RunsTable({ runs }: { runs: IngestorRun[] }) {
    return (
        <table className="lisa__ingestor__table">
            <thead>
                <tr>
                    <th>Tijdstip</th>
                    <th>Status</th>
                    <th>Opgehaald</th>
                    <th>Skipped</th>
                    <th>Fout</th>
                    <th>Ack.</th>
                </tr>
            </thead>
            <tbody>
                {runs.map((run) => (
                    <tr key={run.id} className={`lisa__ingestor__row--${run.status.toLowerCase()}`}>
                        <td>{formatDate(run.createdAt)}</td>
                        <td>
                            <span className={`lisa__badge lisa__badge--ingestor-${run.status.toLowerCase()}`}>
                                {run.status === 'SUCCESS' ? 'Geslaagd' : 'Mislukt'}
                            </span>
                        </td>
                        <td>{run.fetched ?? '—'}</td>
                        <td className="lisa__ingestor__skipped-cell">
                            {run.skipped
                                ? (
                                    <details className="lisa__ingestor__skipped">
                                        <summary>{run.skipped}</summary>
                                        <ul>
                                            {(run.skippedIds ?? []).map((id) => (
                                                <li key={id}>{id}</li>
                                            ))}
                                        </ul>
                                    </details>
                                )
                                : '0'}
                        </td>
                        <td className="lisa__ingestor__error-cell">{run.error ?? '—'}</td>
                        <td>
                            {run.acknowledgedStatus != null
                                ? <span className={`lisa__badge lisa__badge--ingestor-${run.acknowledgedStatus === 201 ? 'success' : 'failed'}`}>{run.acknowledgedStatus}</span>
                                : '—'}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

export default function IngestorStatus() {
    const [autoRefresh, setAutoRefresh] = useState(false);
    const [intervalSeconds, setIntervalSeconds] = useState(10);
    const [activeTab, setActiveTab] = useState<Tab>('recent');

    const { recent, withSkipped, errors, isLoading, refresh, process: proc } = useLisaIngestorStatus(
        autoRefresh ? intervalSeconds * 1000 : 0
    );

    const { found, running, cpu, memoryMb, restarts, uptimeSince } = proc ?? {};

    const last = recent[0] ?? null;
    const hasFailed = last?.status === 'FAILED';

    const tabRuns: Record<Tab, IngestorRun[]> = {
        recent,
        skipped: withSkipped,
        errors,
    };

    return (
        <div className={`lisa__ingestor${hasFailed ? ' lisa__ingestor--error' : ''}`}>
            <div className="lisa__ingestor__header">
                <h2 className="lisa__ingestor__title">
                    Ingestor
                    {hasFailed && <span className="lisa__ingestor__alert" title="Laatste run mislukt">!</span>}
                </h2>
                {found && (
                    <div className="lisa__ingestor__process-controls">
                        <span className={`lisa__badge lisa__badge--process-${running ? 'online' : 'stopped'}`}>
                            {running ? 'Actief' : 'Gestopt'}
                        </span>
                        {running && uptimeSince && (
                            <span className="lisa__ingestor__process-stat" title="Uptime">
                                ⏱ {formatUptime(uptimeSince)}
                            </span>
                        )}
                        {running && cpu !== null && (
                            <span className="lisa__ingestor__process-stat" title="CPU">
                                CPU {cpu}%
                            </span>
                        )}
                        {running && memoryMb !== null && (
                            <span className="lisa__ingestor__process-stat" title="RAM">
                                {memoryMb} MB
                            </span>
                        )}
                        {restarts != null && restarts > 0 && (
                            <span className="lisa__ingestor__process-stat lisa__ingestor__process-stat--warn" title="Restarts">
                                ↺ {restarts}
                            </span>
                        )}
                    </div>
                )}
                <div className="lisa__refresh-controls">
                    <label className="lisa__refresh-toggle">
                        <input
                            type="checkbox"
                            checked={autoRefresh}
                            onChange={(e) => setAutoRefresh(e.target.checked)}
                        />
                        Auto-refresh
                    </label>
                    {autoRefresh && (
                        <select
                            className="lisa__interval-select"
                            value={intervalSeconds}
                            onChange={(e) => setIntervalSeconds(Number(e.target.value))}
                        >
                            {INTERVAL_OPTIONS.map((s) => (
                                <option key={s} value={s}>{s}s</option>
                            ))}
                        </select>
                    )}
                    <button className="lisa__refresh-btn" onClick={() => refresh()} title="Vernieuwen">
                        <FontAwesomeIcon icon={faSync} />
                    </button>
                </div>
            </div>

            {hasFailed && (
                <div className="lisa__ingestor__error-banner">
                    <strong>Communicatiefout</strong> — de ingestor kon geen berichten ophalen bij LISA.
                    {last.error && <span className="lisa__ingestor__error-banner__detail">{last.error}</span>}
                </div>
            )}

            {isLoading && <p className="lisa__loading">Laden…</p>}

            {!isLoading && !last && (
                <p className="lisa__ingestor__empty">Nog geen runs geregistreerd.</p>
            )}

            {last && (
                <div className="lisa__ingestor__last">
                    <span className="lisa__ingestor__last__label">Laatste run</span>
                    <span className="lisa__ingestor__last__time">{formatDate(last.createdAt)}</span>
                    <span className={`lisa__badge lisa__badge--ingestor-${last.status.toLowerCase()}`}>
                        {last.status === 'SUCCESS' ? `Geslaagd · ${last.fetched}` : 'Mislukt'}
                    </span>
                    {last.status === 'SUCCESS' && (last.skipped ?? 0) > 0 && (
                        <span className="lisa__badge lisa__badge--ingestor-skipped">
                            {last.skipped} skipped
                        </span>
                    )}
                </div>
            )}

            {recent.length > 0 && (
                <div className="lisa__ingestor__tabs-section">
                    <div className="lisa__ingestor__tabs">
                        <button
                            className={`lisa__ingestor__tab${activeTab === 'recent' ? ' lisa__ingestor__tab--active' : ''}`}
                            onClick={() => setActiveTab('recent')}
                        >
                            Recent ({recent.length})
                        </button>
                        <button
                            className={`lisa__ingestor__tab${activeTab === 'skipped' ? ' lisa__ingestor__tab--active' : ''}${withSkipped.length > 0 ? ' lisa__ingestor__tab--warn' : ''}`}
                            onClick={() => setActiveTab('skipped')}
                        >
                            Laatste skipped ({withSkipped.length})
                        </button>
                        <button
                            className={`lisa__ingestor__tab${activeTab === 'errors' ? ' lisa__ingestor__tab--active' : ''}${errors.length > 0 ? ' lisa__ingestor__tab--error' : ''}`}
                            onClick={() => setActiveTab('errors')}
                        >
                            Laatste errors ({errors.length})
                        </button>
                    </div>
                    {tabRuns[activeTab].length === 0
                        ? <p className="lisa__ingestor__empty">Geen runs.</p>
                        : <RunsTable runs={tabRuns[activeTab]} />
                    }
                </div>
            )}
        </div>
    );
}
